import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  ExternalLink, 
  BookOpen, 
  Video, 
  Code, 
  Palette, 
  Brain,
  Filter,
  Search,
  Tag,
  Calendar,
  Star
} from 'lucide-react';
import { Resource } from '../../types';

/**
 * Resources section component - Curated collection
 * Features useful resources, articles, tools, and learning materials
 */

const ResourcesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[8]};
`;

const SearchAndFilter = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[4]};
  margin-bottom: ${props => props.theme.spacing[6]};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const SearchInput = styled.div`
  flex: 1;
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]} ${props => props.theme.spacing[3]} ${props => props.theme.spacing[12]};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.typography.sizes.base};
  background-color: ${props => props.theme.colors.surface};
  transition: border-color ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary[500]};
  }

  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${props => props.theme.spacing[4]};
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.textSecondary};
  pointer-events: none;
`;

const FilterButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[2]};
  flex-wrap: wrap;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    justify-content: center;
  }
`;

const FilterButton = styled(motion.button)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  border: 2px solid ${props => props.$active 
    ? props.theme.colors.primary[600] 
    : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.full};
  background-color: ${props => props.$active 
    ? props.theme.colors.primary[600] 
    : 'transparent'};
  color: ${props => props.$active 
    ? 'white' 
    : props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.sm};
  font-weight: ${props => props.theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};

  &:hover {
    border-color: ${props => props.theme.colors.primary[600]};
    background-color: ${props => props.$active 
      ? props.theme.colors.primary[700] 
      : props.theme.colors.primary[50]};
  }
`;

const ResourcesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${props => props.theme.spacing[6]};

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ResourceCard = styled(motion.div)`
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing[6]};
  border: 1px solid ${props => props.theme.colors.border};
  transition: all ${props => props.theme.animations.duration.normal} ${props => props.theme.animations.easing.smooth};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    ${props => props.theme.patterns.geometric}
    opacity: 0.08;
    pointer-events: none;
    z-index: 0;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.lg};
    border-color: ${props => props.theme.colors.primary[200]};
  }
`;

const ResourceHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing[4]};
  margin-bottom: ${props => props.theme.spacing[4]};
  position: relative;
  z-index: 1;
`;

const ResourceHeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${props => props.theme.spacing[3]};
`;

const ResourceTitleContainer = styled.div`
  flex: 1;
  margin-right: ${props => props.theme.spacing[3]};
`;

const ResourceIcon = styled.div<{ $category: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${props => props.theme.borderRadius.lg};
  background: ${props => {
    const colors = {
      development: props.theme.gradients.primary,
      'ai-ml': props.theme.gradients.cosmic,
      design: props.theme.gradients.sunset,
      learning: props.theme.gradients.ocean,
      tools: props.theme.gradients.forest,
    };
    return colors[props.$category as keyof typeof colors] || props.theme.gradients.primary;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const ResourceInfo = styled.div`
  flex: 1;
`;

const ResourceTitle = styled.h3`
  font-size: ${props => props.theme.typography.sizes.lg};
  font-weight: ${props => props.theme.typography.weights.semibold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing[2]};
  line-height: ${props => props.theme.typography.lineHeights.tight};
`;

const ResourceMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
  margin-bottom: ${props => props.theme.spacing[3]};
`;

const ResourceType = styled.span<{ type: string }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[2]};
  background-color: ${props => {
    const colors = {
      article: props.theme.colors.info,
      video: props.theme.colors.secondary.pink,
      tool: props.theme.colors.secondary.green,
      book: props.theme.colors.secondary.orange,
      course: props.theme.colors.secondary.purple,
      other: props.theme.colors.neutral[400],
    };
    return colors[props.type as keyof typeof colors] || props.theme.colors.neutral[400];
  }};
  color: white;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.sizes.xs};
  font-weight: ${props => props.theme.typography.weights.medium};
  text-transform: uppercase;
`;

const ResourceDate = styled.span`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.sizes.sm};
`;

const ResourceDescription = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
  margin-bottom: ${props => props.theme.spacing[4]};
  position: relative;
  z-index: 1;
`;

const ResourceTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[2]};
  margin-bottom: ${props => props.theme.spacing[4]};
  position: relative;
  z-index: 1;
`;

const ResourceTag = styled.span`
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[2]};
  background-color: ${props => props.theme.colors.primary[100]};
  color: ${props => props.theme.colors.primary[700]};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.sizes.xs};
  font-weight: ${props => props.theme.typography.weights.medium};
`;

const ResourceAction = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  background: ${props => props.theme.gradients.primary};
  color: white;
  text-decoration: none;
  border-radius: ${props => props.theme.borderRadius.lg};
  font-weight: ${props => props.theme.typography.weights.medium};
  font-size: ${props => props.theme.typography.sizes.sm};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const FeaturedBadge = styled.div`
  background: ${props => props.theme.gradients.cosmic};
  color: white;
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.sizes.xs};
  font-weight: ${props => props.theme.typography.weights.bold};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  align-self: flex-start;
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing[12]};
  color: ${props => props.theme.colors.textSecondary};
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing[4]};
  color: ${props => props.theme.colors.textSecondary};
`;

// Resources data
const resources: Resource[] = [
  {
    id: '1',
    title: 'React TypeScript Cheatsheet',
    description: 'Comprehensive guide to using TypeScript with React, covering best practices, common patterns, and advanced techniques.',
    url: 'https://react-typescript-cheatsheet.netlify.app/',
    category: 'development',
    tags: ['react', 'typescript', 'guide', 'cheatsheet'],
    type: 'article',
    featured: true,
    addedDate: '2024-09-15'
  },
  {
    id: '2',
    title: 'Framer Motion Tutorial',
    description: 'Learn how to create beautiful animations and micro-interactions with Framer Motion for React applications.',
    url: 'https://www.youtube.com/watch?v=2V1WK-3HQNk',
    category: 'development',
    tags: ['framer-motion', 'animations', 'react', 'tutorial'],
    type: 'video',
    featured: true,
    addedDate: '2024-09-10'
  },
  {
    id: '3',
    title: 'AI/ML for Web Developers',
    description: 'Introduction to machine learning concepts and how to integrate AI capabilities into web applications.',
    url: 'https://www.coursera.org/learn/machine-learning',
    category: 'ai-ml',
    tags: ['ai', 'machine-learning', 'web-development', 'integration'],
    type: 'course',
    featured: false,
    addedDate: '2024-09-08'
  },
  {
    id: '4',
    title: 'Design System Best Practices',
    description: 'Essential principles for building scalable and maintainable design systems for modern web applications.',
    url: 'https://storybook.js.org/docs/react/writing-docs/design-system',
    category: 'design',
    tags: ['design-system', 'ui-ux', 'scalability', 'best-practices'],
    type: 'article',
    featured: false,
    addedDate: '2024-09-05'
  },
  {
    id: '5',
    title: 'Node.js Performance Optimization',
    description: 'Advanced techniques for optimizing Node.js applications for better performance and scalability.',
    url: 'https://nodejs.org/en/docs/guides/simple-profiling/',
    category: 'development',
    tags: ['nodejs', 'performance', 'optimization', 'backend'],
    type: 'article',
    featured: false,
    addedDate: '2024-09-03'
  },
  {
    id: '6',
    title: 'MongoDB Atlas Tutorial',
    description: 'Complete guide to setting up and using MongoDB Atlas for cloud database management.',
    url: 'https://www.mongodb.com/atlas/database',
    category: 'development',
    tags: ['mongodb', 'database', 'cloud', 'tutorial'],
    type: 'video',
    featured: false,
    addedDate: '2024-09-01'
  }
];

const categories = [
  { id: 'all', label: 'All', icon: Filter },
  { id: 'development', label: 'Development', icon: Code },
  { id: 'ai-ml', label: 'AI/ML', icon: Brain },
  { id: 'design', label: 'Design', icon: Palette },
];

const ResourceTypeIcon = ({ type }: { type: string }) => {
  const icons = {
    article: BookOpen,
    video: Video,
    tool: Code,
    book: BookOpen,
    course: BookOpen,
    other: ExternalLink,
  };
  const Icon = icons[type as keyof typeof icons] || ExternalLink;
  return <Icon size={12} />;
};

const CategoryIcon = ({ category }: { category: string }) => {
  const icons = {
    development: Code,
    'ai-ml': Brain,
    design: Palette,
    learning: BookOpen,
    tools: Code,
  };
  const Icon = icons[category as keyof typeof icons] || Code;
  return <Icon size={20} />;
};

const Resources: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <motion.div
      key={selectedCategory} // Force re-render when category changes
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <ResourcesContainer>
        <SearchAndFilter>
          <SearchInput>
            <SearchIcon>
              <Search size={20} />
            </SearchIcon>
            <Input
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchInput>
          
          <FilterButtons>
            {categories.map((category) => (
              <FilterButton
                key={category.id}
                $active={selectedCategory === category.id}
                onClick={() => setSelectedCategory(category.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <category.icon size={16} />
                {category.label}
              </FilterButton>
            ))}
          </FilterButtons>
        </SearchAndFilter>

        {filteredResources.length > 0 ? (
          <ResourcesGrid>
            {filteredResources.map((resource, index) => (
              <ResourceCard
                key={resource.id}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <ResourceHeader>
                  <ResourceIcon $category={resource.category}>
                    <CategoryIcon category={resource.category} />
                  </ResourceIcon>
                  
                  <ResourceInfo>
                    <ResourceHeaderTop>
                      <ResourceTitleContainer>
                        <ResourceTitle>{resource.title}</ResourceTitle>
                      </ResourceTitleContainer>
                      {resource.featured && (
                        <FeaturedBadge>
                          <Star size={12} />
                          Featured
                        </FeaturedBadge>
                      )}
                    </ResourceHeaderTop>
                    
                    <ResourceMeta>
                      <ResourceType type={resource.type}>
                        <ResourceTypeIcon type={resource.type} />
                        {resource.type}
                      </ResourceType>
                      
                      <ResourceDate>
                        <Calendar size={12} />
                        {new Date(resource.addedDate).toLocaleDateString()}
                      </ResourceDate>
                    </ResourceMeta>
                  </ResourceInfo>
                </ResourceHeader>

                <ResourceDescription>{resource.description}</ResourceDescription>

                <ResourceTags>
                  {resource.tags.map((tag) => (
                    <ResourceTag key={tag}>
                      <Tag size={10} />
                      {tag}
                    </ResourceTag>
                  ))}
                </ResourceTags>

                <ResourceAction
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ExternalLink size={16} />
                  Visit Resource
                </ResourceAction>
              </ResourceCard>
            ))}
          </ResourcesGrid>
        ) : (
          <EmptyState>
            <EmptyIcon>
              <Search size={32} />
            </EmptyIcon>
            <h3>No resources found</h3>
            <p>Try adjusting your search terms or filters</p>
          </EmptyState>
        )}
      </ResourcesContainer>
    </motion.div>
  );
};

export default Resources;
